/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef, useState } from 'react';
import { FileArchive, ShieldCheck } from 'lucide-react';
import { chamarApi } from '@/nucleo/auth/cliente-api';
import { Alert, AlertDescription } from '@/ui/base/alert';
import { Button } from '@/ui/base/button';
import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';
import { EmptyState } from '@/ui/EmptyState';
import { PageHeader } from '@/ui/PageHeader';

const NS = '/bff/v1/j13-s01-inventariar-fontes';
type Envelope = { state: any };
type Form = {
  sistema: string;
  versaoFonte: string;
  tabelaOrigem: string;
  sensibilidade: string;
  responsavel: string;
  responsavelContato: string;
  autorizacaoReferencia: string;
  baseLegal: string;
  extracaoSomenteLeitura: boolean;
};
const vazio: Form = {
  sistema: '',
  versaoFonte: '',
  tabelaOrigem: '',
  sensibilidade: 'INTERNO',
  responsavel: '',
  responsavelContato: '',
  autorizacaoReferencia: '',
  baseLegal: '',
  extracaoSomenteLeitura: true,
};

export function InventariarFontesPage() {
  const [estado, setEstado] = useState<any>(),
    [erro, setErro] = useState(''),
    [ocupado, setOcupado] = useState(false),
    [arquivo, setArquivo] = useState<File | null>(null),
    [form, setForm] = useState<Form>(vazio);
  const chave = useRef(crypto.randomUUID());
  useEffect(() => {
    void chamarApi<Envelope>(`${NS}/state`)
      .then((x) => setEstado(x.state))
      .catch(() => setErro('Não foi possível carregar o inventário.'));
  }, []);
  const mudar = (campo: keyof Form, valor: string | boolean) =>
    setForm((atual) => ({ ...atual, [campo]: valor }));
  async function enviar() {
    if (!arquivo) return;
    setOcupado(true);
    setErro('');
    try {
      const bytes = new Uint8Array(await arquivo.arrayBuffer());
      const checksum = Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', bytes)))
        .map((x) => x.toString(16).padStart(2, '0'))
        .join('');
      let binario = '';
      for (let i = 0; i < bytes.length; i += 0x8000)
        binario += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
      const resposta = await chamarApi<Envelope>(`${NS}/verbs/inventariar_fonte`, {
        metodo: 'POST',
        corpo: {
          ...form,
          nomeArquivo: arquivo.name,
          conteudoBase64: btoa(binario),
          checksumDeclarado: checksum,
          chaveIdempotencia: chave.current,
        },
      });
      setEstado(resposta.state);
    } catch {
      setErro('Não foi possível inventariar a fonte. Revise os dados e tente novamente.');
    } finally {
      setOcupado(false);
    }
  }
  if (!estado)
    return (
      <EmptyState
        variant={erro ? 'error' : 'default'}
        title={erro || 'Carregando inventário…'}
        description={erro ? 'Tente novamente em instantes.' : 'Aguarde.'}
      />
    );
  return (
    <section className="min-w-0 max-w-[calc(100vw-2rem)] space-y-5 overflow-hidden">
      <PageHeader title={estado.titulo} description={estado.descricao} />
      {[...estado.alertas.map((a: any) => a.mensagem), ...(erro ? [erro] : [])].map((m: string) => (
        <Alert key={m}>
          <AlertDescription>{m}</AlertDescription>
        </Alert>
      ))}
      <article className="grid gap-4 rounded-md border p-4" aria-labelledby="nova-fonte">
        <h2 id="nova-fonte" className="font-semibold">
          Identificar fonte e versão
        </h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <Campo
            id="sistema"
            rotulo="Sistema de origem"
            valor={form.sistema}
            mudar={(x) => mudar('sistema', x)}
          />
          <Campo
            id="versao"
            rotulo="Versão da fonte"
            valor={form.versaoFonte}
            mudar={(x) => mudar('versaoFonte', x)}
          />
          <Campo
            id="tabela"
            rotulo="Tabela ou conjunto"
            valor={form.tabelaOrigem}
            mudar={(x) => mudar('tabelaOrigem', x)}
          />
          <Campo
            id="dono"
            rotulo="Dono responsável"
            valor={form.responsavel}
            mudar={(x) => mudar('responsavel', x)}
          />
          <Campo
            id="contato"
            rotulo="Contato do responsável"
            valor={form.responsavelContato}
            mudar={(x) => mudar('responsavelContato', x)}
          />
          <Campo
            id="autorizacao"
            rotulo="Referência da autorização"
            valor={form.autorizacaoReferencia}
            mudar={(x) => mudar('autorizacaoReferencia', x)}
          />
          <Campo
            id="base-legal"
            rotulo="Base legal"
            valor={form.baseLegal}
            mudar={(x) => mudar('baseLegal', x)}
          />
          <div>
            <Label htmlFor="sensibilidade">Sensibilidade</Label>
            <select
              id="sensibilidade"
              className="h-10 w-full rounded-md border bg-background px-3"
              value={form.sensibilidade}
              onChange={(e) => mudar('sensibilidade', e.target.value)}
            >
              <option value="PUBLICO">Público</option>
              <option value="INTERNO">Interno</option>
              <option value="PESSOAL">Pessoal</option>
              <option value="PESSOAL_SENSIVEL">Pessoal sensível</option>
            </select>
          </div>
        </div>
        <div>
          <Label htmlFor="arquivo">Extração CSV</Label>
          <Input
            id="arquivo"
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => {
              setArquivo(e.target.files?.[0] ?? null);
              chave.current = crypto.randomUUID();
            }}
          />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.extracaoSomenteLeitura}
            onChange={(e) => mudar('extracaoSomenteLeitura', e.target.checked)}
          />
          Confirmo que a extração é imutável e foi obtida com acesso somente leitura
        </label>
        <Button disabled={ocupado || !arquivo} onClick={() => void enviar()}>
          <ShieldCheck className="mr-2 h-4 w-4" />
          Validar e versionar inventário
        </Button>
      </article>
      <section aria-labelledby="fontes">
        <h2 id="fontes" className="mb-3 font-semibold">
          Universo aprovado
        </h2>
        {estado.inventarios.length === 0 ? (
          <EmptyState
            icon={FileArchive}
            title="Nenhuma fonte aprovada"
            description="Identifique uma extração autorizada para iniciar o mapeamento."
          />
        ) : (
          <div className="grid gap-3">
            {estado.inventarios.map((i: any) => (
              <button
                type="button"
                key={i.id}
                className="w-full rounded-md border p-4 text-left focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() =>
                  void chamarApi<Envelope>(`${NS}/verbs/selecionar_inventario`, {
                    metodo: 'POST',
                    corpo: { inventarioId: i.id },
                  }).then((x) => setEstado(x.state))
                }
              >
                <strong>
                  Versão {i.numero} · {i.sistema}/{i.tabela_origem}
                </strong>
                <span className="mt-1 block text-sm">
                  Fonte {i.versao_fonte} · {i.volume_registros} registro(s) · {i.sensibilidade}
                </span>
                <span className="mt-2 block break-all font-mono text-xs text-muted-foreground">
                  SHA-256 {i.checksum_sha256}
                </span>
              </button>
            ))}
          </div>
        )}
      </section>
      {estado.detalhe && (
        <aside className="rounded-md border p-4">
          <h2 className="font-semibold">Detalhe permitido da versão {estado.detalhe.numero}</h2>
          <p className="mt-2 text-sm">
            Dono: {estado.detalhe.responsavel} ({estado.detalhe.responsavel_contato})
          </p>
          <p className="text-sm">
            Autorização: {estado.detalhe.autorizacao_referencia} · Base legal:{' '}
            {estado.detalhe.base_legal}
          </p>
          <p className="text-sm">Campos: {estado.detalhe.campos.join(', ')}</p>
          <p className="mt-2 break-all font-mono text-xs">
            Hash do inventário: {estado.detalhe.inventario_hash}
          </p>
        </aside>
      )}
      {estado.recusas.length > 0 && (
        <section>
          <h2 className="font-semibold">Fontes bloqueadas</h2>
          {estado.recusas.map((r: any) => (
            <p className="mt-2 rounded-md border border-destructive p-3 text-sm" key={r.id}>
              {r.nome_arquivo}: {r.motivos.join(' ')}
            </p>
          ))}
        </section>
      )}
    </section>
  );
}
function Campo({
  id,
  rotulo,
  valor,
  mudar,
}: {
  id: string;
  rotulo: string;
  valor: string;
  mudar: (v: string) => void;
}) {
  return (
    <div>
      <Label htmlFor={id}>{rotulo}</Label>
      <Input id={id} value={valor} onChange={(e) => mudar(e.target.value)} />
    </div>
  );
}
